import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useStore } from '../pages/workspace/hooks';
import { getNorms } from '../store/selectors/user.selector';
import {
  PDF_PHOTOMETRY_ENTRIES,
  type PdfPhotometryType,
  type PhotometrySelection,
  isPhotometryIncluded,
} from '../utils/pdfPlotGeneration/photometryMapping';
import { createLayerTitle, getIlluminatedLayers } from '../utils/pdfUtils';
import { ExportContext } from './ExportContextDefinition';

export const ExportProvider = ({ children }: { children: ReactNode }) => {
  const { activeVersion } = useStore();
  const norms = useSelector(getNorms);

  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({
    'cover-page': true,
    'table-of-content': true,
    'lamp-info': true,
    'layers-summary': true,
    graphs: false,
    'global-plots': true,
  });

  const [selectedMeasurements, setSelectedMeasurements] = useState<Record<string, boolean>>({});
  const [numPages, setNumPages] = useState<number | null>(null);

  // Every photometry is exported until the user deselects it.
  const [selectedPhotometries, setSelectedPhotometries] = useState<PhotometrySelection>({});

  // Effect to populate selectedMeasurements with all illuminated layers by default
  useEffect(() => {
    if (activeVersion && activeVersion.meshes && norms.length > 0) {
      const illuminatedLayers = getIlluminatedLayers(activeVersion);

      const newSelectedMeasurements: Record<string, boolean> = {};
      illuminatedLayers.forEach((mesh) => {
        const layerTitle = createLayerTitle(
          mesh.displayName || mesh.name,
          mesh.normId,
          norms,
          mesh.targets?.length ? 'custom' : undefined,
        );
        newSelectedMeasurements[layerTitle] = true; // Set all to true by default
      });

      setSelectedMeasurements(newSelectedMeasurements);
    }
  }, [activeVersion, norms]);

  const handleMeasurementCheck = useCallback((id: string) => {
    setSelectedMeasurements((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const handleCheck = useCallback((id: string) => {
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // At least one photometry stays selected, so the plots and tables never end up empty.
  const handlePhotometryCheck = useCallback((photometry: PdfPhotometryType) => {
    setSelectedPhotometries((prev) => {
      const included = isPhotometryIncluded(prev, photometry);
      const selectedCount = PDF_PHOTOMETRY_ENTRIES.filter((e) => isPhotometryIncluded(prev, e.photometry)).length;
      if (included && selectedCount <= 1) return prev;
      return { ...prev, [photometry]: !included };
    });
  }, []);

  const value = useMemo(
    () => ({
      checkedItems,
      handleCheck,
      selectedPhotometries,
      handlePhotometryCheck,
      selectedMeasurements,
      handleMeasurementCheck,
      numPages,
      setNumPages,
    }),
    [
      checkedItems,
      handleCheck,
      selectedPhotometries,
      handlePhotometryCheck,
      selectedMeasurements,
      handleMeasurementCheck,
      numPages,
      setNumPages,
    ],
  );

  return <ExportContext.Provider value={value}>{children}</ExportContext.Provider>;
};
